import React, { useState } from 'react';
import { SharedLink } from './models/shared-link.model';
import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MoreVertical, Copy, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import { UserAvatar } from '@/components/ui/user-avatar';
import { useI18n } from '@/hooks/useI18n';
import { useToast } from '@/hooks/useToast';
import { useNavigate } from 'react-router-dom';

interface SharedLinksTableProps {
  links: SharedLink[];
  onEdit: (link: SharedLink) => void;
  onDelete: (id: string) => void;
  isLoading?: boolean;
}

export const SharedLinksTable: React.FC<SharedLinksTableProps> = ({
  links,
  onEdit,
  onDelete,
  isLoading,
}) => {
  const { t } = useI18n();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState<Record<string, boolean>>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLinkId, setSelectedLinkId] = useState<string | null>(null);

  const togglePasswordVisibility = (id: string) => {
    setShowPassword((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const copyToClipboard = (link: SharedLink) => {
    const shareUrl = `${window.location.origin}/#/shared/${link.token}`;
    navigator.clipboard.writeText(shareUrl);
    toast({ variant: 'info',
      title: t('shared.linkCopied'),
      description: shareUrl,
    });
  };

  const handleDeleteClick = (id: string) => {
    setSelectedLinkId(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (selectedLinkId) {
      await onDelete(selectedLinkId);
    }
    setDeleteDialogOpen(false);
    setSelectedLinkId(null);
  };

  const handleItineraryClick = (itineraryId: string) => {
    navigate(`/itinerary-builder?itineraryId=${itineraryId}`);
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const columns: DataTableColumn<SharedLink>[] = [
    {
      id: 'itinerary',
      // Nessuna larghezza: in una <table table-fixed> e' l'unica colonna senza width,
      // quindi assorbe lo spazio residuo. Stessa scelta della colonna titolo in
      // LibraryActivitiesTable.
      header: t('shared.itinerary'),
      cellClassName: 'font-medium break-words',
      cell: (link) => (
        <button
          onClick={() => handleItineraryClick(link.itinerary_id)}
          className="text-left text-gray-900 hover:underline"
        >
          {link.itinerary_name}
        </button>
      ),
    },
    {
      id: 'users',
      header: t('shared.users'),
      headClassName: 'w-[20%]',
      // Versione estesa di UserAvatar: cerchio con le iniziali (lo stesso colore che la
      // persona ha in dashboard e nelle richieste) seguito dal nome in chiaro. Le due
      // righe sono cliente e advisor, nell'ordine dell'intestazione; email e ruolo nel
      // tooltip.
      cell: (link) => (
        <div className="flex flex-col items-start gap-1.5">
          {link.client_name || link.client_email ? (
            <UserAvatar
              expanded
              fullName={link.client_name}
              email={link.client_email}
              imageUrl={link.client_avatar_url}
              size="sm"
              showTooltip
              hint={t('shared.client')}
            />
          ) : null}
          <UserAvatar
            expanded
            fullName={link.designer_name}
            email={link.designer_email}
            imageUrl={link.designer_avatar_url}
            size="sm"
            showTooltip
            hint={t('shared.advisor')}
          />
        </div>
      ),
    },
    {
      id: 'password',
      header: t('shared.password'),
      headClassName: 'w-[12%]',
      cell: (link) =>
        link.password ? (
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm">
              {showPassword[link.id] ? link.password : '••••••••'}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => togglePasswordVisibility(link.id)}
              className="h-6 w-6 p-0"
            >
              {showPassword[link.id] ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </Button>
          </div>
        ) : (
          <span className="text-muted-foreground italic">-</span>
        ),
    },
    {
      id: 'expiration',
      header: t('shared.expiration'),
      headClassName: 'w-[14%]',
      cell: (link) => (
        <div className="flex flex-col gap-1">
          {link.expires_at ? (
            <>
              <span className="text-sm">
                {formatDate(new Date(link.expires_at))}
              </span>
              <Badge
                variant={link.is_active ? 'default' : 'destructive'}
                className="w-fit text-xs"
              >
                {link.is_active ? t('shared.active') : t('shared.expired')}
              </Badge>
            </>
          ) : (
            <span className="text-muted-foreground italic text-sm">
              {t('shared.noExpiration')}
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'link',
      header: t('shared.link'),
      headClassName: 'w-[16%]',
      // L'URL intero occupava un terzo della tabella per mostrare un prefisso identico
      // su ogni riga. In cella resta la sola parte che distingue il link (il token),
      // l'indirizzo completo sta nel tooltip e nel pulsante copia.
      cell: (link) => {
        const shareUrl = `${window.location.origin}/#/shared/${link.token}`;
        return (
          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="min-w-0 flex-1 truncate font-mono text-xs text-muted-foreground cursor-help">
                  /shared/{link.token}
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-md">
                <p className="font-mono text-xs break-all">{shareUrl}</p>
              </TooltipContent>
            </Tooltip>
            <Button
              variant="outline"
              size="sm"
              onClick={() => copyToClipboard(link)}
              className="h-7 px-2 shrink-0"
              aria-label={t('shared.copyLink')}
            >
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <TooltipProvider>
      <DataTable<SharedLink>
        data={links}
        columns={columns}
        getRowId={(link) => link.id}
        // table-fixed comprime le colonne: su mobile serve una larghezza minima che
        // attivi lo scroll orizzontale, su desktop il 100% resta invariato.
        tableClassName="min-w-[980px]"
        actionsHeader={t('common.actions')}
        actionsHeadClassName="w-[64px]"
        renderActions={(link) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(link)}>
                <Edit className="mr-2 h-4 w-4" />
                {t('common.edit')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleDeleteClick(link.id)}
                className="text-red-600"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t('common.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        isLoading={isLoading}
        emptyState={t('shared.noLinks')}
      />

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('shared.deleteLink')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('shared.confirmDelete')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
                {t('common.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </TooltipProvider>
  );
};
